import queryString from 'query-string';
import type {
	ErrorResponse,
	ParsedQueryParameters,
	BoardLocationResult,
	BoardQueryParameters,
	CachedFilters,
} from './types';

const SOFTWARE_BOARD_REGEX = /\/jira\/software\/c\/?projects\/\w+\/boards\/(\d+)\/?(\?.*)?$/;
const USER_LOCATED_BOARD_REGEX = /\/jira\/people\/[^/]+\/boards\/(\d+)\/?(\?.*)?$/;

// people/project match needs to consider `...:...` ids
const SOFTWARE_BACKLOG_REGEX =
	/\/jira\/(?:software\/c\/)?(?:projects|people)\/[^/]+\/boards\/(\d+)\/backlog\/?(\?.*)?$/;

const parsePath = (pathname: string, pattern: RegExp): { boardId: number } | null => {
	const match = pattern.exec(pathname);
	if (!match) {
		return null;
	}
	const boardId = match[1];
	return { boardId: Number(boardId) };
};

// Safety check to guarantee that whatever we add here is an array
export const convertQuickFiltersToString = (
	queryParameters: BoardQueryParameters | undefined,
): string | undefined => {
	// Terminate early for the null/undefined cases
	if (!queryParameters?.quickFilter) {
		return undefined; // This is safe to return as it means the query string will just be excluded
	}

	if (!Array.isArray(queryParameters.quickFilter)) {
		// If for whatever reason an object gets in here
		if (typeof queryParameters.quickFilter === 'object') {
			// Fail gracefully
			return undefined;
		}

		// Ensure that the value is a number
		if (Number.isNaN(Number(queryParameters.quickFilter))) return undefined;

		// this isn't an array. It's probably a string or number which is fine to return as is.
		return `${queryParameters.quickFilter}`;
	}

	// Array passed in is the common case
	// If it is empty, don't include it at all.
	if (queryParameters.quickFilter.length === 0) {
		return undefined;
	}

	return queryParameters.quickFilter.filter((val) => Number.isInteger(Number(val))).join(',');
};

export const getCachedQuickFilters = (feature: 'board' | 'backlog', boardId: string | number) => {
	const cachedFilters = localStorage.getItem(`__storejs_software_${feature}-filters.${boardId}`);
	if (!cachedFilters) {
		return undefined;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { CUSTOM_FILTER: cachedQuickFilters } = JSON.parse(cachedFilters) as CachedFilters;
	return cachedQuickFilters && cachedQuickFilters.length > 0
		? cachedQuickFilters.join(',')
		: undefined;
};

export const parseBoardLocation = ({ pathname, search }: Location): BoardLocationResult | null => {
	const parsedPath =
		parsePath(pathname, SOFTWARE_BOARD_REGEX) ?? parsePath(pathname, USER_LOCATED_BOARD_REGEX);
	if (!parsedPath) {
		return null;
	}

	const queryStringParameters = queryString.parse(search) ?? {};

	// Early termination if the quick filter isnt a part of the query string
	if (!queryStringParameters.quickFilter) {
		return {
			...parsedPath,
		};
	}

	// Guarantee that the quick filter will be returned as an array. This helps with typing further upstream
	const quickFilterArray = Array.isArray(queryStringParameters.quickFilter)
		? queryStringParameters.quickFilter
		: [queryStringParameters.quickFilter];

	return {
		...parsedPath,
		queryParameters: {
			quickFilter: quickFilterArray,
		},
	};
};

export const parseBacklogLocation = ({
	pathname,
	search,
}: Location): {
	boardId: number;
	queryParameters: ParsedQueryParameters;
} | null => {
	const parsedPath = parsePath(pathname, SOFTWARE_BACKLOG_REGEX);
	if (!parsedPath) {
		return null;
	}

	return {
		...parsedPath,
		queryParameters: queryString.parse(search) ?? {},
	};
};

const isErrorMessagesResponse = (json: unknown): json is ErrorResponse =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	(json as ErrorResponse)?.errorMessages instanceof Array;

export const extractErrorMessage = (json: unknown): string | undefined => {
	if (isErrorMessagesResponse(json)) {
		const { errorMessages } = json;
		return errorMessages.length ? errorMessages.join(', ') : undefined;
	}
	return undefined;
};
